import { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { wrapper } from '../api/store'
import { Provider } from 'react-redux'
import * as gtag from '../libs/utils/ga'
import '../static/styles/globals.scss'

declare global {
    interface Window {
        WebFont: any
    }
}

/**
 * JOTA_UNICO
 *
 * Este App define a lógica global do site, incluindo Redux, Google Analytics e WebFont.
 *
 * 🚫 Além disso, intercepta URLs com fragmentos do tipo `#!`, que foram geradas incorretamente
 * e estão sendo rastreadas pelo Google como se fossem páginas distintas.
 *
 * Como fragmentos (hash) nunca são enviados ao servidor, o Next.js (via middleware ou SSR)
 * não consegue tratá-los diretamente. Portanto, esta lógica client-side redireciona essas URLs
 * para `/hash-trash`, uma rota que retorna HTTP 410 e <meta name="robots" content="noindex">.
 *
 * Isso garante que:
 *  - A URL poluída não seja indexada
 *  - O conteúdo canônico da home não seja impactado
 *  - A remoção futura pelo Google aconteça corretamente
 *
 * @param {any} Component
 * @param {any} pageProps
 * @return {React.ReactElement}
 */
export default function JOTA_UNICO({ Component, pageProps }: AppProps) {
    const router = useRouter()
    const { store } = wrapper.useWrappedStore(pageProps)

    useEffect(() => {
        /**
         * Trata URLs com fragmentos `#!`, redirecionando para rota de descarte `/hash-trash`
         * Essa verificação só roda no client-side, após o React montar.
         */
        const redirectIfHashInvalid = () => {
            const hash = window.location.hash
            if (hash.startsWith('#!')) {
                window.location.replace('/hash-trash')
            }
        }

        if (typeof window !== 'undefined') {
            // Redireciona imediatamente
            redirectIfHashInvalid()
            // Reforço após 100ms (garante execução em casos de carregamento lento)
            setTimeout(redirectIfHashInvalid, 100)

            // Carrega WebFonts customizadas (caso necessário)
            if (window.WebFont) {
                window.WebFont.load({
                    custom: {
                        families: ['Roboto:300,400,500,700']
                    }
                })
            }
        }

        // Rastreia pageviews no GA após mudanças de rota
        const handleRouteChange = (url: string) => {
            if (process.env.NODE_ENV === 'production') {
                gtag.pageview(url)
            }
        }

        router.events.on('routeChangeComplete', handleRouteChange)
        return () => {
            router.events.off('routeChangeComplete', handleRouteChange)
        }
    }, [router])

    return (
        <Provider store={store}>
            <Component {...pageProps} />
        </Provider>
    )
}
